import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Snowflake, ShieldCheck, Mail, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import Brand from "@/components/Brand";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COMPANY } from "@/lib/company";
import api, { apiError } from "@/lib/api";

const HERO =
  "https://images.unsplash.com/photo-1764148506729-97e80b3d4c28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxydiUyMHdpbnRlciUyMHNub3d8ZW58MHx8fHwxNzg0NDk1NzI2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(null); // {name}
  const [sent, setSent] = useState(false);

  const onContinue = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setNeedsSetup(null);
    try {
      const { data } = await api.post("/auth/check-email", { email: email.trim() });
      if (!data.exists) {
        navigate(`/register?email=${encodeURIComponent(email.trim())}`);
      } else if (data.needs_setup && data.role !== "admin") {
        setNeedsSetup({ name: data.name });
      } else {
        navigate(`/login?email=${encodeURIComponent(email.trim())}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendSetup = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success("Setup email sent. Check your inbox to activate your account.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="glass sticky top-0 z-30 border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Brand />
          <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}>
            Log In
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <img src={HERO} alt="RV in winter" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-primary/30" />
        <div className="relative z-10 mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
          >
            <span className="font-mono-label inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] text-white/80">
              <Snowflake className="h-3 w-3" /> Winterizing 2026 Now Open
            </span>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Winterize your RV with Shoreline RV.
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-white/85">
              Enter your email to get started. New customers create an account; returning
              customers verify their details and reserve in a couple of clicks.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/80">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Secure accounts</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Fast signup</span>
            </div>
          </motion.div>

          {/* Email check card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center"
          >
            <div className="glass w-full rounded-2xl border border-white/40 p-8 shadow-xl">
              <h2 className="font-heading text-xl font-semibold text-primary">Get Started</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll check if you already have an account.
              </p>

              {!needsSetup ? (
                <form onSubmit={onContinue} className="mt-6 space-y-4">
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="landing-email-input"
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                  <Button
                    data-testid="landing-continue-btn"
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full rounded-full text-base"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1 h-4 w-4" /></>}
                  </Button>
                </form>
              ) : (
                <div className="mt-6 space-y-4" data-testid="landing-setup-panel">
                  <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-4 text-sm text-foreground">
                    Welcome back{needsSetup.name ? `, ${needsSetup.name}` : ""}! Your account needs to be
                    activated. We'll email you a link to set your password.
                  </div>
                  <Button
                    data-testid="landing-send-setup-btn"
                    disabled={loading || sent}
                    onClick={sendSetup}
                    className="h-12 w-full rounded-full"
                  >
                    {sent ? "Setup Email Sent ✓" : loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Setup Email"}
                  </Button>
                  <button
                    className="text-sm text-muted-foreground underline"
                    onClick={() => { setNeedsSetup(null); setSent(false); }}
                  >
                    Use a different email
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "Your info, verified", desc: "Confirm your contact and RV details before every season." },
            { icon: Snowflake, title: "Add-on services", desc: "Ice maker, washer, water softener and more — priced automatically." },
            { icon: CheckCircle2, title: "Auto sign-up", desc: "Opt in once and we'll enroll you every winterizing season." },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="rounded-xl border border-border bg-card p-6"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent/10">
                <f.icon className="h-5 w-5 text-accent" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-primary">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About / Company */}
      <section className="border-t border-border bg-card" data-testid="about-section">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-2">
          <div>
            <p className="font-mono-label text-xs text-muted-foreground">About Us</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-primary">
              About Shoreline RV
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Shoreline RV provides professional RV winterizing services for RV owners
              across the lakes area. With decades of experience and thousands of winterizations completed, we help protect
              your investment through the off-season.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-background p-6">
            <h3 className="font-heading text-lg font-semibold text-primary">Business Information</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Brand (DBA)</dt>
                <dd className="font-medium text-foreground">{COMPANY.brand}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Legal entity</dt>
                <dd className="font-medium text-foreground" data-testid="about-legal-name">{COMPANY.legalName}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Contact</dt>
                <dd>
                  <a href={`mailto:${COMPANY.contactEmail}`} className="font-medium text-secondary underline">
                    {COMPANY.contactEmail}
                  </a>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Website</dt>
                <dd className="font-medium text-foreground">{COMPANY.website}</dd>
              </div>
            </dl>
            <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground" data-testid="about-ownership">
              {COMPANY.ownershipStatement}
            </p>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
